/* Music Courses — hierarchical Tree view (default).
   3 levels: Category → Master Series → Course (with levels inside).
   Full CRUD at every level + select-all/deselect-all + bulk fee adjust
   (with increase/decrease + apply-to-all) + Kanban toggle. */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { useList, errMsg, fmtRM, mediaUrl } from "@/lib/api";
import { PageHeader, Spinner, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ChevronRight, ChevronDown, Plus, Pencil, Trash2, FolderPlus, LayoutGrid, Settings,
  TrendingUp, Download, FolderTree, ListChecks, AlertCircle, GraduationCap, Eye, EyeOff,
  ShieldCheck, CheckSquare, Square,
} from "lucide-react";
import {
  CourseEditDialog, BulkFeeAdjustDialog, CsvToolsDialog,
} from "@/pages/admin/CoursesBoardPage";

const PROTECTED_CATEGORIES = ["Piano", "Guitar", "Drum", "Vocal", "Violin", "Saxophone",
                              "Electone", "Junior Music", "Music Theory", "Other"];

// ---------- Build the hierarchical tree ----------
function buildTree(courses, fees) {
  const feeByCourse = {};
  // Only consider course-level fees (level_id is null) — level-specific fees
  // are for legacy / nested levels and shouldn't show up as the course headline.
  (fees || []).forEach((f) => {
    if (f.status === "active" && (f.level_id === null || f.level_id === undefined)) {
      feeByCourse[f.course_id] = f.music_course_fee;
    }
  });
  // category → master → courses[]
  const tree = new Map();
  PROTECTED_CATEGORIES.forEach((c) => tree.set(c, new Map()));
  (courses || []).forEach((c) => {
    const cat = (c.category || "Other").trim() || "Other";
    const master = (c.master_course_name || "").trim();
    if (!tree.has(cat)) tree.set(cat, new Map());
    const masters = tree.get(cat);
    const key = master || "__standalone__";
    if (!masters.has(key)) masters.set(key, []);
    masters.get(key).push({ ...c, fee: feeByCourse[c.id] });
  });
  // Sort
  for (const [, masters] of tree) {
    for (const [, list] of masters) {
      list.sort((a, b) => {
        const al = a.level_within_master ?? 0;
        const bl = b.level_within_master ?? 0;
        if (al !== bl) return al - bl;
        return (a.name || "").localeCompare(b.name || "");
      });
    }
  }
  return [...tree.entries()].sort((a, b) => a[0].localeCompare(b[0]))
    .map(([cat, mastersMap]) => [
      cat,
      [...mastersMap.entries()]
        .sort(([a], [b]) => (a === "__standalone__" ? 1 : b === "__standalone__" ? -1 : a.localeCompare(b))),
    ]);
}

// ---------- Rename dialog (used for category + master) ----------
function RenameDialog({ open, label, oldName, onClose, onConfirm }) {
  const [name, setName] = useState(oldName || "");
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="rename-dialog">
        <DialogHeader>
          <DialogTitle>Rename {label}</DialogTitle>
          <DialogDescription>All courses in &quot;{oldName}&quot; will move to the new name.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label className="text-xs">New name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)}
                 data-testid="rename-input" autoFocus />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={busy || !name.trim() || name === oldName}
                  data-testid="rename-confirm"
                  onClick={async () => {
                    setBusy(true);
                    try { await onConfirm(name.trim()); } finally { setBusy(false); }
                  }}>
            {busy ? "Renaming…" : "Rename"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Category management dialog (list + actions) ----------
function CategoryManagerDialog({ open, onClose, tree, onChanged }) {
  const [newCat, setNewCat] = useState("");
  const [renameTarget, setRenameTarget] = useState(null);
  if (!open) return null;
  const categories = tree.map(([cat]) => cat);

  const addCategory = () => {
    if (!newCat.trim()) return;
    if (categories.includes(newCat.trim())) { toast.error("Category exists"); return; }
    toast.success(`Empty category "${newCat.trim()}" — drag/move a course here to fill it`);
    setNewCat("");
    onChanged(newCat.trim());
  };

  const renameCategory = async (oldName, newName) => {
    try {
      await api.post("/categories/rename", { old_name: oldName, new_name: newName });
      toast.success(`Renamed "${oldName}" → "${newName}"`);
      setRenameTarget(null);
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-md" data-testid="category-manager-dialog">
          <DialogHeader>
            <DialogTitle>Manage categories</DialogTitle>
            <DialogDescription>Rename or add a category. To delete, move its courses elsewhere first.</DialogDescription>
          </DialogHeader>
          <div className="flex items-end gap-2 mb-3">
            <div className="flex-1 space-y-1.5">
              <Label className="text-xs">New category</Label>
              <Input value={newCat} onChange={(e) => setNewCat(e.target.value)}
                     placeholder="e.g. Brass" data-testid="new-category-name" />
            </div>
            <Button onClick={addCategory} data-testid="add-category-confirm">
              <Plus className="h-4 w-4 mr-1" />Add
            </Button>
          </div>
          <div className="border rounded-md divide-y max-h-72 overflow-y-auto">
            {tree.map(([cat, masters]) => {
              const total = masters.reduce((s, [, list]) => s + list.length, 0);
              return (
                <div key={cat} className="flex items-center justify-between px-3 py-2 text-sm"
                     data-testid={`cat-mgmt-row-${cat}`}>
                  <span>
                    <span className="font-medium">{cat}</span>
                    <span className="text-xs text-muted-foreground ml-2">{total} course{total !== 1 ? "s" : ""}</span>
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => setRenameTarget(cat)}
                          data-testid={`rename-cat-${cat}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
      <RenameDialog open={!!renameTarget} label="category" oldName={renameTarget}
                    onClose={() => setRenameTarget(null)}
                    onConfirm={(newName) => renameCategory(renameTarget, newName)} />
    </>
  );
}

// ---------- Main page ----------
export default function CoursesTreePage() {
  const { data: courses, loading, reload: reloadCourses } = useList("/courses");
  const { data: fees, reload: reloadFees } = useList("/course-fees");
  const [expandedCats, setExpandedCats] = useState(new Set());
  const [expandedMasters, setExpandedMasters] = useState(new Set());
  const [selected, setSelected] = useState(new Set());
  const [editCourse, setEditCourse] = useState(null);
  const [addCourse, setAddCourse] = useState(null); // {category, master_course_name} prefill
  const [bulkOpen, setBulkOpen] = useState(false);
  const [csvOpen, setCsvOpen] = useState(false);
  const [catMgrOpen, setCatMgrOpen] = useState(false);
  const [renameMaster, setRenameMaster] = useState(null); // string

  const tree = useMemo(() => buildTree(courses || [], fees || []), [courses, fees]);
  const allMasters = useMemo(() =>
    [...new Set((courses || []).map((c) => c.master_course_name).filter(Boolean))].sort()
  , [courses]);
  const allIds = useMemo(() => (courses || []).map((c) => c.id), [courses]);

  if (loading || !courses) return <Spinner full />;

  const toggleCat = (cat) => {
    const next = new Set(expandedCats);
    next.has(cat) ? next.delete(cat) : next.add(cat);
    setExpandedCats(next);
  };
  const toggleMaster = (key) => {
    const next = new Set(expandedMasters);
    next.has(key) ? next.delete(key) : next.add(key);
    setExpandedMasters(next);
  };
  const expandAll = () => {
    setExpandedCats(new Set(tree.map(([c]) => c)));
    const masterKeys = [];
    tree.forEach(([cat, masters]) => masters.forEach(([m]) => masterKeys.push(`${cat}//${m}`)));
    setExpandedMasters(new Set(masterKeys));
  };
  const collapseAll = () => { setExpandedCats(new Set()); setExpandedMasters(new Set()); };

  // Selection helpers
  const toggleOne = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const selectMany = (ids, on) => {
    const next = new Set(selected);
    if (on) ids.forEach((i) => next.add(i));
    else ids.forEach((i) => next.delete(i));
    setSelected(next);
  };
  const allSelected = allIds.length > 0 && selected.size === allIds.length;
  const someSelected = selected.size > 0 && selected.size < allIds.length;

  // Bulk delete
  const bulkPublish = async (publish) => {
    if (selected.size === 0) return;
    const verb = publish ? "show on public website" : "hide from public";
    if (!window.confirm(`${publish ? "Show" : "Hide"} ${selected.size} course(s) ${publish ? "on" : "from"} the public catalogue?`)) return;
    try {
      const r = await api.post("/admin/courses/bulk-publish",
        { mode: "by_ids", course_ids: [...selected], publish });
      toast.success(`${r.data.updated || 0} course(s) updated`);
      setSelected(new Set());
      reloadCourses();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} course(s)? This cannot be undone.`)) return;
    const ids = [...selected];
    let okCount = 0;
    const blocked = [];
    for (const id of ids) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await api.delete(`/courses/${id}`);
        okCount += 1;
      } catch (e) {
        const d = e?.response?.data?.detail;
        const name = (courses.find((c) => c.id === id) || {}).name || id;
        if (d && typeof d === "object" && d.in_use) {
          blocked.push(`"${name}": ${Object.entries(d.in_use).map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`).join(", ")}`);
        } else {
          blocked.push(`"${name}": ${errMsg(e)}`);
        }
      }
    }
    if (okCount) toast.success(`Deleted ${okCount} course(s)`);
    if (blocked.length) {
      toast.error(`Could not delete ${blocked.length}:\n${blocked.slice(0, 3).join("\n")}${blocked.length > 3 ? `\n…and ${blocked.length - 3} more` : ""}`,
                   { duration: 8000 });
    }
    setSelected(new Set());
    reloadCourses(); reloadFees();
  };

  // Delete a single course
  const deleteCourse = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/courses/${c.id}`);
      toast.success(`Deleted "${c.name}"`);
      const next = new Set(selected); next.delete(c.id); setSelected(next);
      reloadCourses(); reloadFees();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.in_use) {
        const lines = Object.entries(d.in_use).map(([k, v]) => `• ${v} ${k.replace(/_/g, " ")}`);
        toast.error(`Cannot delete "${c.name}":\n${lines.join("\n")}\n\nMove / cancel them first.`,
                     { duration: 8000 });
      } else {
        toast.error(errMsg(e));
      }
    }
  };

  // Rename a master
  const doRenameMaster = async (oldName, newName) => {
    try {
      await api.post("/masters/rename", { old_name: oldName, new_name: newName });
      toast.success(`Renamed "${oldName}" → "${newName}"`);
      setRenameMaster(null);
      reloadCourses();
    } catch (e) { toast.error(errMsg(e)); }
  };

  // Counts
  const totalCourses = (courses || []).length;
  const totalMasters = allMasters.length;
  const totalCategories = tree.length;

  return (
    <div data-testid="courses-tree-page">
      <PageHeader title="Music Courses"
        description="Categories → master series → individual courses. Add, edit, delete, move and bulk-adjust fees from one place."
        actions={
          <div className="flex flex-wrap gap-2">
            <Link to="/admin/courses-board">
              <Button variant="outline" size="sm" data-testid="kanban-view-btn">
                <LayoutGrid className="h-4 w-4 mr-1" />Kanban view
              </Button>
            </Link>
            <Button size="sm" onClick={() => setAddCourse({})} data-testid="add-course-btn">
              <Plus className="h-4 w-4 mr-1" />Add course
            </Button>
          </div>
        } />

      {/* Stats + toolbar */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div className="rounded-lg border bg-card p-3" data-testid="stat-categories">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Categories</p>
          <p className="text-2xl font-bold">{totalCategories}</p>
        </div>
        <div className="rounded-lg border bg-card p-3" data-testid="stat-masters">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Master series</p>
          <p className="text-2xl font-bold">{totalMasters}</p>
        </div>
        <div className="rounded-lg border bg-card p-3" data-testid="stat-courses">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Total courses</p>
          <p className="text-2xl font-bold">{totalCourses}</p>
        </div>
        <div className="rounded-lg border bg-card p-3" data-testid="stat-selected">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Selected</p>
          <p className="text-2xl font-bold">{selected.size}</p>
        </div>
      </div>

      <div className="rounded-lg border bg-card p-3 mb-3 flex flex-wrap items-center gap-2"
           data-testid="tree-toolbar">
        <Button variant={allSelected ? "default" : "outline"} size="sm"
                onClick={() => setSelected(new Set(allIds))}
                disabled={allIds.length === 0 || allSelected}
                data-testid="select-all-btn"
                className={allSelected ? "" : "border-purple-300 text-purple-800 hover:bg-purple-50"}>
          <CheckSquare className="h-4 w-4 mr-1" />
          Select all ({allIds.length})
        </Button>
        <Button variant="outline" size="sm"
                onClick={() => setSelected(new Set())}
                disabled={selected.size === 0}
                data-testid="deselect-all-btn"
                className="text-slate-700 hover:bg-slate-50">
          <Square className="h-4 w-4 mr-1" />
          Deselect all
        </Button>
        <span className="h-6 w-px bg-slate-200 mx-1" aria-hidden />
        <Button variant="outline" size="sm" onClick={expandAll} data-testid="expand-all-btn">
          <ChevronDown className="h-4 w-4 mr-1" />Expand all
        </Button>
        <Button variant="outline" size="sm" onClick={collapseAll} data-testid="collapse-all-btn">
          <ChevronRight className="h-4 w-4 mr-1" />Collapse
        </Button>
        <Button variant="outline" size="sm" onClick={() => setCatMgrOpen(true)} data-testid="manage-cat-btn">
          <FolderTree className="h-4 w-4 mr-1" />Manage categories
        </Button>
        <Button variant="outline" size="sm" onClick={() => window.location.href = "/admin/courses-audit"} data-testid="open-audit-btn">
          <ShieldCheck className="h-4 w-4 mr-1" />Data integrity audit
        </Button>
        <Button variant="outline" size="sm" onClick={async () => {
          if (!window.confirm("Download all 12 Cornerstone course images and link them to matching courses? (Skips courses that already have images.)")) return;
          try {
            const r = await api.post("/admin/courses/import-cornerstone-images");
            const total = (r.data.items || []).reduce((s, it) => s + (it.attached || 0), 0);
            toast.success(`Imported ${(r.data.items || []).length} images · attached to ${total} courses`);
            reloadCourses();
          } catch (e) { toast.error(errMsg(e)); }
        }} data-testid="bulk-import-images-btn">
          <FolderTree className="h-4 w-4 mr-1" />Import Cornerstone images
        </Button>
        <Button variant="outline" size="sm" onClick={async () => {
          if (!window.confirm("Publish ALL active courses with a simplified name to the public website?\n\nThis flips public_visible=ON for courses that already have a public-friendly display name (e.g. Classical Piano, Drum, Flute, etc.). You can still hide individual courses later via the visibility toggle on each row.")) return;
          try {
            const r = await api.post("/admin/courses/bulk-publish", { mode: "with_simplified_name", publish: true });
            toast.success(`Published ${r.data.updated} courses to the public site (${r.data.matched} already had a simplified name)`);
            reloadCourses();
          } catch (e) { toast.error(errMsg(e)); }
        }} data-testid="bulk-publish-btn">
          <Eye className="h-4 w-4 mr-1" />Publish all to public site
        </Button>
        <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)} data-testid="bulk-fee-btn">
          <TrendingUp className="h-4 w-4 mr-1" />Bulk adjust fees
        </Button>
        <Button variant="outline" size="sm" onClick={() => setCsvOpen(true)} data-testid="csv-tools-btn">
          <Download className="h-4 w-4 mr-1" />CSV import / export
        </Button>
        <Link to="/admin/fee-settings">
          <Button variant="outline" size="sm" data-testid="admin-fee-link">
            <Settings className="h-4 w-4 mr-1" />Admin / SST fees
          </Button>
        </Link>
      </div>

      {/* Floating action bar when items selected */}
      {selected.size > 0 && (
        <div className="rounded-lg border border-secondary bg-secondary/10 p-3 mb-3 flex flex-wrap items-center gap-2"
             data-testid="selection-actionbar">
          <ListChecks className="h-4 w-4 text-secondary" />
          <span className="text-sm font-semibold">{selected.size} course{selected.size === 1 ? "" : "s"} selected</span>
          <span className="text-xs text-muted-foreground ml-2">
            (use bulk-fee dialog to adjust their fees; or)
          </span>
          <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-adjust-selected-btn">
            <TrendingUp className="h-3.5 w-3.5 mr-1" />Adjust fees
          </Button>
          <Button size="sm" variant="outline" onClick={() => bulkPublish(true)}
                  data-testid="bulk-show-public-btn"
                  className="text-emerald-700 hover:text-emerald-800 border-emerald-200">
            <Eye className="h-3.5 w-3.5 mr-1" />Show public
          </Button>
          <Button size="sm" variant="outline" onClick={() => bulkPublish(false)}
                  data-testid="bulk-hide-public-btn"
                  className="text-slate-700 hover:text-slate-800 border-slate-300">
            <EyeOff className="h-3.5 w-3.5 mr-1" />Hide from public
          </Button>
          <Button size="sm" variant="outline" onClick={bulkDelete}
                  data-testid="bulk-delete-selected-btn"
                  className="text-rose-700 hover:text-rose-800 border-rose-200">
            <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}
                  data-testid="clear-selection-btn">Clear</Button>
        </div>
      )}

      {/* The tree */}
      <div className="rounded-lg border bg-card" data-testid="courses-tree">
        {/* Header row */}
        <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
          <Checkbox checked={allSelected ? true : someSelected ? "indeterminate" : false}
                    onCheckedChange={(v) => v ? setSelected(new Set(allIds)) : setSelected(new Set())}
                    data-testid="tree-select-all" />
          <span className="flex-1">Course</span>
          <span className="w-20 text-right">Duration</span>
          <span className="w-24 text-right">Tuition</span>
          <span className="w-24 text-right">Public</span>
          <span className="w-24 text-right">Actions</span>
        </div>

        {tree.map(([cat, masters]) => {
          const catIds = masters.flatMap(([, list]) => list.map((c) => c.id));
          const catSelected = catIds.length > 0 && catIds.every((id) => selected.has(id));
          const catSome = catIds.some((id) => selected.has(id)) && !catSelected;
          const isExpanded = expandedCats.has(cat);
          const total = catIds.length;
          return (
            <div key={cat} data-testid={`tree-cat-${cat}`}>
              {/* Category row */}
              <div className="flex items-center gap-2 px-3 py-2.5 border-b bg-muted/10 hover:bg-muted/30 transition">
                <Checkbox checked={catSelected ? true : catSome ? "indeterminate" : false}
                          onCheckedChange={(v) => selectMany(catIds, !!v)}
                          data-testid={`select-cat-${cat}`} />
                <button onClick={() => toggleCat(cat)} className="flex items-center gap-1 flex-1 text-left"
                        data-testid={`toggle-cat-${cat}`}>
                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  <FolderTree className="h-4 w-4 text-secondary" />
                  <span className="text-sm font-bold uppercase tracking-wider">{cat}</span>
                  <span className="text-xs text-muted-foreground ml-2">{total} course{total !== 1 ? "s" : ""}</span>
                </button>
                <Button size="sm" variant="ghost" onClick={() => setAddCourse({ category: cat })}
                        data-testid={`add-to-cat-${cat}`} title="Add course in this category">
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>

              {/* Master rows */}
              {isExpanded && masters.map(([masterKey, list]) => {
                const isStandalone = masterKey === "__standalone__";
                const masterId = `${cat}//${masterKey}`;
                const mExpanded = expandedMasters.has(masterId);
                const mIds = list.map((c) => c.id);
                const mSelected = mIds.length > 0 && mIds.every((id) => selected.has(id));
                const mSome = mIds.some((id) => selected.has(id)) && !mSelected;
                return (
                  <div key={masterId} data-testid={`tree-master-${cat}-${masterKey}`}>
                    {!isStandalone && (
                      <div className="flex items-center gap-2 px-3 py-2 pl-10 border-b bg-card hover:bg-muted/20 transition">
                        <Checkbox checked={mSelected ? true : mSome ? "indeterminate" : false}
                                  onCheckedChange={(v) => selectMany(mIds, !!v)}
                                  data-testid={`select-master-${masterKey}`} />
                        <button onClick={() => toggleMaster(masterId)}
                                className="flex items-center gap-1 flex-1 text-left"
                                data-testid={`toggle-master-${masterKey}`}>
                          {mExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          <GraduationCap className="h-4 w-4 text-amber-700" />
                          <span className="text-sm font-semibold">{masterKey}</span>
                          <span className="text-xs text-muted-foreground ml-2">{list.length} level{list.length !== 1 ? "s" : ""}</span>
                        </button>
                        <Button size="sm" variant="ghost"
                                onClick={() => setAddCourse({ category: cat, master_course_name: masterKey })}
                                title="Add a level under this master"
                                data-testid={`add-to-master-${masterKey}`}>
                          <Plus className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setRenameMaster(masterKey)}
                                data-testid={`rename-master-${masterKey}`} title="Rename master">
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                    {/* Course rows */}
                    {(isStandalone || mExpanded) && list.map((c) => (
                      <div key={c.id}
                           className={`flex items-center gap-2 px-3 py-2 border-b transition
                                       ${isStandalone ? "pl-10" : "pl-16"}
                                       ${selected.has(c.id) ? "bg-emerald-50/30" : "hover:bg-muted/10"}`}
                           data-testid={`tree-course-${c.id}`}>
                        <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleOne(c.id)}
                                  data-testid={`select-course-${c.id}`} />
                        {c.image_url ? (
                          <img src={mediaUrl(c.image_url)} alt={c.image_alt_text || c.name}
                               className="h-8 w-12 rounded object-cover border shrink-0"
                               loading="lazy"
                               data-testid={`thumb-${c.id}`} />
                        ) : (
                          <span className="h-8 w-12 rounded border bg-muted/40 flex items-center justify-center shrink-0">
                            <FolderTree className="h-3 w-3 text-muted-foreground" />
                          </span>
                        )}
                        <button onClick={() => setEditCourse(c)} className="flex-1 text-left min-w-0"
                                data-testid={`open-course-${c.id}`}>
                          <p className="text-sm font-medium truncate">
                            {c.name}
                            {c.level_within_master ? <span className="text-xs text-muted-foreground ml-1">· level {c.level_within_master}</span> : null}
                          </p>
                          {c.description && (
                            <p className="text-[10px] text-muted-foreground truncate">{c.description}</p>
                          )}
                        </button>
                        <span className="w-20 text-right text-xs"
                              data-testid={`duration-${c.id}`}>
                          {c.class_type === "group" ? (
                            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 border border-violet-200">
                              Group
                            </span>
                          ) : null}
                          <span className="text-muted-foreground ml-1">{c.duration_minutes || (c.class_type === "group" ? 60 : 30)} min</span>
                        </span>
                        <span className="w-24 text-right font-mono text-sm">
                          {c.fee ? fmtRM(c.fee) : <span className="text-[10px] text-amber-700">no fee</span>}
                        </span>
                        <span className="w-24 text-right">
                          {c.public_visible ? (
                            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-2 py-0.5">PUBLIC</span>
                          ) : (
                            <span className="text-[10px] text-muted-foreground">hidden</span>
                          )}
                        </span>
                        <div className="w-24 flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => setEditCourse(c)}
                                  data-testid={`edit-course-${c.id}`} title="Edit">
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteCourse(c)}
                                  data-testid={`delete-course-${c.id}`} title="Delete">
                            <Trash2 className="h-3.5 w-3.5 text-rose-600" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          );
        })}
        {tree.length === 0 && <EmptyState text="No courses yet. Click 'Add course' to start." />}
      </div>

      <p className="mt-3 text-xs text-muted-foreground flex items-center gap-1.5"
         data-testid="tree-help">
        <AlertCircle className="h-3 w-3" />
        Tip: tick boxes at the category / master / course level then use &quot;Adjust fees&quot; for selected courses,
        or use the bulk dialog to apply changes to a whole category or ALL courses.
      </p>

      {/* Dialogs */}
      <CourseEditDialog
        open={!!editCourse || !!addCourse}
        course={editCourse || (addCourse ? {
          id: null, name: "", category: addCourse.category || "",
          master_course_name: addCourse.master_course_name || "",
          level_within_master: null, public_visible: false, status: "active",
          fee: null, description: "",
        } : null)}
        onClose={() => { setEditCourse(null); setAddCourse(null); }}
        onSaved={() => {
          setEditCourse(null); setAddCourse(null);
          reloadCourses(); reloadFees();
        }} />

      <BulkFeeAdjustDialog open={bulkOpen} onClose={() => setBulkOpen(false)}
                            categories={tree.map(([c]) => c)}
                            masters={allMasters}
                            selectedIds={[...selected]}
                            onSaved={() => { setBulkOpen(false); reloadFees(); }} />

      <CsvToolsDialog open={csvOpen} onClose={() => setCsvOpen(false)}
                       onApplied={() => { setCsvOpen(false); reloadCourses(); reloadFees(); }} />

      <CategoryManagerDialog open={catMgrOpen} onClose={() => setCatMgrOpen(false)}
                              tree={tree} onChanged={() => reloadCourses()} />

      <RenameDialog open={!!renameMaster} label="master series" oldName={renameMaster}
                    onClose={() => setRenameMaster(null)}
                    onConfirm={(newName) => doRenameMaster(renameMaster, newName)} />
    </div>
  );
}
